import { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { Link, useLoaderData, useRouteLoaderData } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import AutoplayCarousel from "~/components/ui/AutoplayCarousel";
import { Button } from "~/components/ui/Button";
import {
  CarouselContent,
  CarouselDots,
  CarouselItem,
} from "~/components/ui/Carousel";
import { getLocale } from "~/lib/i18n.server";
import { createSitemapFunction } from "~/lib/sitemap-generator";
import { getMenuHref, getMetaData, isEnabled } from "~/lib/utils";

export const sitemap = createSitemapFunction("massages");

export const meta: MetaFunction = (data: any) => {
  const { t } = useTranslation();
  return getMetaData(data, {
    title: t("Massaggi"),
    description: t(
      "Regalati un momento di puro relax con i nostri massaggi. Scegli tra un'ampia gamma di trattamenti, dal rilassante al decontratturante, e affidati alle mani esperte dei nostri terapisti."
    ),
  });
};

export async function loader({ context, request }: LoaderFunctionArgs) {
  try {
    const pageSlug = "massaggi";

    const locale = await getLocale(request);
    const lang = locale;
    const res = await fetch(
      `${context.cloudflare.env.WP_API_HOST}/wp-json/wp/v2/pages?slug=${pageSlug}&lang=${lang}`
    );
    const pages: any = await res.json();
    if (!res.ok) {
      throw new Error(`Failed to fetch: ${res.statusText}`);
    }
    if (pages && pages.length > 0) {
      return {
        page: pages[0],
        seo: pages[0]?.yoast_head_json,
        lang,
      };
    } else {
      return {
        error: "No pages found for the given slug.",
        page: {},
      };
    }
  } catch (err) {
    return {
      err: err,
      page: {},
    };
  }
}

export default function Massages() {
  const loaderData = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation();
  const { bookingDisabled, shopDisabled } = useRouteLoaderData<any>("root");

  return (
    <div
      className={`
        relative
      `}
    >
      <main className="flex flex-col lg:pb-12">
        {loaderData.page.acf?.hero && (
          <section
            className="bg-cover bg-center bg-no-repeat "
            style={{
              backgroundImage: `url('/cms-asset/${loaderData.page.acf?.hero.image?.id}')`,
            }}
          >
            <div className="bg-black bg-opacity-40 py-32">
              <div className="flex flex-col max-w-4xl mx-auto items-center justify-center">
                <h1 className="text-4xl lg:text-5xl text-white font-tenor-sans uppercase">
                  {loaderData.page.acf?.hero.title}
                </h1>
                <hr className="block w-16 h-[2px] bg-white mt-2" />
                <p className="text-white text-lg mt-4">
                  {loaderData.page.acf?.hero.description}
                </p>
              </div>
            </div>
          </section>
        )}

        {loaderData.page && loaderData.page.acf?.intro && (
          <section className="w-full">
            <div className="flex flex-col justify-center text-center max-w-5xl mx-auto py-12 px-4">
              <div className="flex flex-col items-center">
                <h4 className="text-secondary max-w-2xl font-tenor-sans text-2xl lg:text-4xl uppercase">
                  {loaderData.page.acf?.intro.title}
                </h4>
                <div
                  className="mt-4"
                  dangerouslySetInnerHTML={{
                    __html: loaderData.page.acf?.intro.description,
                  }}
                />
                <span className="text-secondary italic text-lg font-semibold mt-4">
                  {loaderData.page.acf?.intro.phrase}
                </span>
              </div>
            </div>
          </section>
        )}

        {loaderData.page && loaderData.page.acf?.info_section_1 && (
          <section className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
              <div>
                <div className="w-full h-96">
                  <span className="text-4xl font-semibold">
                    <img
                      alt=""
                      src={`/cms-asset/${loaderData.page.acf?.info_section_1.image}`}
                      className="w-full h-full object-cover object-center"
                    />
                  </span>
                </div>
              </div>
              <div className="flex flex-col">
                <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                  {loaderData.page.acf?.info_section_1.title}
                </h4>
                <div
                  className="mt-4"
                  dangerouslySetInnerHTML={{
                    __html: loaderData.page.acf?.info_section_1.description,
                  }}
                />
              </div>
            </div>
          </section>
        )}

        {loaderData.page && loaderData.page.acf?.info_section_2 && (
          <section className="w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
              <div className="flex flex-col">
                <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                  {loaderData.page.acf?.info_section_2.title}
                </h4>
                <div
                  className="mt-4"
                  dangerouslySetInnerHTML={{
                    __html: loaderData.page.acf?.info_section_2.description,
                  }}
                />
              </div>
              <div>
                <AutoplayCarousel className="w-full">
                  <CarouselContent>
                    {loaderData.page.acf?.info_section_2.gallery?.map(
                      (_: any, index: number) => (
                        <CarouselItem key={index}>
                          <div className="w-full h-96">
                            <span className="text-4xl font-semibold">
                              <img
                                alt=""
                                src={`/cms-asset/${_?.id}`}
                                className="w-full h-full object-cover object-center"
                              />
                            </span>
                          </div>
                        </CarouselItem>
                      )
                    )}
                  </CarouselContent>
                  <CarouselDots className="relative top-0 bottom-0 mt-4" />
                </AutoplayCarousel>
              </div>
            </div>
          </section>
        )}

        {loaderData.page.acf?.info_section_3 && (
          <section className="w-full mt-12">
            <div
              className="bg-cover bg-center bg-no-repeat w-full"
              style={{
                backgroundImage: `url('/cms-asset/${loaderData.page.acf?.info_section_3.image?.id}')`,
              }}
            >
              <div className="bg-black bg-opacity-20 py-32 lg:py-52 px-4">
                <div className="flex flex-col max-w-3xl mx-auto items-center justify-center">
                  <h1 className="text-2xl lg:text-4xl text-center font-tenor-sans text-white">
                    {loaderData.page.acf?.info_section_3.title}
                  </h1>
                  <h3 className="text-xl text-center font-tenor-sans text-white font-semibold mt-4">
                    {loaderData.page.acf?.info_section_3.subtitle}
                  </h3>
                  <div
                    className="text-white mt-4 text-md text-center"
                    dangerouslySetInnerHTML={{
                      __html: loaderData.page.acf?.info_section_3.description,
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {loaderData.page.acf?.treatments &&
          loaderData.page.acf?.treatments.find((treatment: any) =>
            isEnabled(treatment.treatments_enable)
          ) && (
            <section className="bg-white-nude w-full py-12">
              <div className="max-w-7xl mx-auto w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                  {loaderData.page.acf?.treatments.map(
                    (treatment: any, index: number) => (
                      <div key={index} className="flex flex-col p-4 bg-muted">
                        <img
                          alt=""
                          src={`/cms-asset/${treatment.image.id}`}
                          className="w-full lg:w-72 h-72 object-cover object-center"
                        />
                        <h4 className="text-black text-2xl font-tenor-sans mt-6">
                          {treatment.title}
                        </h4>
                        <hr className="block w-12 h-[2px] bg-secondary mt-2" />
                        <p className="text-sm mt-2">{treatment.description}</p>
                      </div>
                    )
                  )}
                </div>
              </div>
            </section>
          )}

        {loaderData.page.acf?.booking_section && (
          <section className="max-w-7xl mx-auto w-full lg:mt-12">
            <div
              className="bg-cover bg-center bg-no-repeat w-full"
              style={{
                backgroundImage: `url('/cms-asset/${loaderData.page.acf?.booking_section.image?.id}')`,
              }}
            >
              <div className="bg-black bg-opacity-20 py-20 px-4">
                <div className="flex flex-col max-w-3xl mx-auto items-center justify-center">
                  <h1 className="text-4xl text-center font-tenor-sans text-white">
                    {loaderData.page.acf?.booking_section.title}
                  </h1>
                  <p className="text-white mt-4 text-xl text-center">
                    {loaderData.page.acf?.booking_section.description}
                  </p>
                  <div className="flex items-center justify-center space-x-2">
                    <Link
                      to={
                        bookingDisabled
                          ? getMenuHref(i18n.language, "/coming-soon")
                          : getMenuHref(i18n.language, "/booking")
                      }
                    >
                      <Button variant={"muted"} className="mt-4">
                        {t("Prenota")}
                      </Button>
                    </Link>
                    <Link
                      to={
                        shopDisabled
                          ? getMenuHref(i18n.language, "/coming-soon")
                          : getMenuHref(i18n.language, "/shop")
                      }
                    >
                      <Button variant={"muted"} className="mt-4">
                        {t("Regala")}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
